// Dev-only floating reset button — never renders in production builds
import { useState, useContext } from 'react';
import { RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@tasted/utils/supabase';
import { GameFlowContext } from '../../contexts/GameFlowProvider';
import { playerStorage, claimTokenStorage, pendingJoinTokenStorage } from '../../utils/playerStorage';

const __DEV__ = import.meta.env.DEV;

export default function DevResetButton({ sessionCode: sessionCodeProp } = {}) {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const ctx = useContext(GameFlowContext);
  const sessionCode = sessionCodeProp || ctx?.sessionCode;
  const navigate = useNavigate();

  if (!__DEV__ || !sessionCode) return null;

  const handleReset = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: session, error: sessionError } = await supabase
        .from('sessions_v2')
        .select('type')
        .eq('id', sessionCode)
        .single();

      if (sessionError || !session) throw new Error('Session not found');
      if (session.type === 'prod') throw new Error('Cannot reset production sessions');

      // One server-side step (migration 121, TAS-19): scores_v2/answers_v2 are
      // read-only for clients now, so the reset lives in a SECURITY DEFINER
      // RPC that refuses production sessions itself.
      const { error: resetError } = await supabase.rpc('reset_test_session', {
        p_session_id: sessionCode,
        p_anchor: true,
      });
      if (resetError) throw new Error(resetError.message);

      // Review note: this used to leave playerStorage/claimTokenStorage/
      // pendingJoinTokenStorage's in-memory mirrors populated with the
      // pre-reset identity — the raw store.clear() calls below wipe the
      // persisted copies, but the mirrors added for TAS-423 don't know that
      // happened, and `get()` would keep answering with the stale mirrored
      // value for the rest of this session.
      const myPlayerId = playerStorage.get();
      playerStorage.clear();
      if (myPlayerId) claimTokenStorage.clear(myPlayerId);
      pendingJoinTokenStorage.clearAll();

      localStorage.clear();
      sessionStorage.clear();

      // Back to join with code pre-filled
      navigate(`/?code=${sessionCode}`, { replace: true });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="fixed left-4 z-[9998] flex items-center justify-center w-10 h-10 rounded-full bg-purple-600 hover:bg-purple-700 active:scale-95 text-white shadow-lg transition-colors"
        style={{ bottom: 'max(1rem, calc(env(safe-area-inset-bottom, 0px) + 0.75rem))' }}
        title="Reset game (dev only)"
      >
        <RotateCcw className="h-5 w-5" />
      </button>

      {showModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                <RotateCcw className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Reset Game?</h2>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              This will clear all answers, scores, and players from session{' '}
              <strong className="font-semibold">{sessionCode}</strong>.
            </p>
            <p className="text-sm text-gray-500 mb-5">
              You'll be returned to the join screen with the code pre-filled.
            </p>
            {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setError(null);
                }}
                disabled={loading}
                className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                disabled={loading}
                className="flex-1 py-2.5 px-4 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
              >
                {loading ? 'Resetting…' : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
